<div class="container">
  <div class="container-card">
    <mat-card appearance="outlined" class="mat-component-background mat-elevation-z2">
      <mat-card-content>
        <p><strong>This page is the updated Privacy Policy; for the version effective until August 4th, 2025, click <a
              href="/privacy-policy-31-july-2024">here</a>.</strong></p>
      </mat-card-content>
    </mat-card>
  </div>

  <h1>Privacy Policy</h1>

  <div class="container-last-updated">
    <em>Effective Date: August 4th, 2025 Last updated: July 1st, 2025</em>
  </div>

  <p>
    At Planet VG, we’re committed to building a more vegan-friendly world—while keeping your data safe and secure. This
    privacy policy explains how we handle your data, whether you’re a user or one of our valued partners using Planet VG
    to connect with customers.
  </p>
  <p>
    This policy applies to your use of the Planet VG apps and websites. So, grab a cup of oat latte, and let’s dive in.
  </p>
  <p>
    Important: You must be at least 16 years old to use our services. We do not knowingly collect or maintain personal
    data about any person under the age of 16.
  </p>

  <h2>1. Who are we?</h2>
  <p>
    Planet VG is a trade name owned by Almost Level 5 B.V., based in The Hague. We’re responsible for processing your
    data in compliance with the GDPR and Dutch data protection laws.
  </p>
  <p><strong>Contact us anytime:</strong></p>
  <p>
    <strong>Email:</strong> privacy&#64;almostlevel5.com<br />
    <strong>Address:</strong> Oder 20 Unit E2574, 2491DC The Hague (the Netherlands)<br />
    <strong>Chamber of Commerce No.:</strong> 93949138<br />
  </p>

  <h2>2. How do we use your data, and why?</h2>
  <p>In short, we use your data to:</p>
  <ul>
    <li>Create and manage your Planet VG account with your (personal or
      business) details.</li>
    <li>Provide personalized rewards like birthday, veguation and
      veganniversary vouchers.</li>
    <li>Offer location-based promotions and relevant rewards when you're
      near a partner’s location.</li>
    <li>Improve the app experience through user and partner feedback.</li>
    <li>Send newsletters and marketing messages.</li>
    <li>Allow partners to optimize their vegan-friendly offerings through
      analytical tools.</li>
    <li>Keep accounts secure.</li>
    <li>Communicate with you about our services or promotions.</li>
    <li>Process payments via Stripe (we only receive confirmation of payment
      success or failure and the last 4 digits of your card).</li>
  </ul>

  <h3>For users</h3>
  <ul>
    <li>Name: So, we can personalize emails and messages.</li>
    <li>Email: For account creation, verification, and (marketing) communication.</li>
    <li>Birthday: To let partners send you a birthday reward and to ensure you are eligible to create an account.</li>
    <li>Veganniversary: Celebrate your vegan journey with special rewards.</li>
    <li>Veguation: Celebrate your vegetarian journey with special rewards.</li>
    <li>Gender: So, partners can send tailored offers (e.g., ladies-only events).</li>
    <li>Location data: For location-based promotions and offers.</li>
    <li>Dietary preferences: Vegan, vegetarian, flexitarian, or others.</li>
    <li>Username and password.</li>
    <li>Any additional information you share with Planet VG.</li>
  </ul>

  <h3>For partners</h3>
  <ul>
    <li>Business information and contact details (personal or business).</li>
    <li>Username and password.</li>
    <li>Payment and billing details.</li>
    <li>Any additional information you share with Planet VG.</li>
  </ul>

  <h2>3. What’s the legal basis for all of this?</h2>
  <p>
    Under the General Data Protection Regulation (GDPR), we process your personal data based on the following legal
    grounds:
  </p>
  <p><strong>1. Performance of a contract </strong></p>
  <ul>
    <li>To create and manage your account.</li>
    <li>To provide the services you’ve requested, such as subscriptions.</li>
    <li>To process payments securely via Stripe.</li>
  </ul>

  <p><strong>Consent</strong></p>
  <ul>
    <li>To send newsletters.</li>
    <li>To use your location data for personalized rewards.</li>
  </ul>

  <p><strong>Legimate interests</strong></p>
  <ul>
    <li>To improve our apps, websites and services by analyzing feedback and usage data.</li>
    <li>To ensure account security and prevent fraudulent activity.</li>
  </ul>

  <p><strong>Compliance with legal obligations </strong></p>
  <ul>
    <li>To comply with laws requiring us to retain payment information for financial record-keeping.</li>
    <li>To respond to lawful requests from public authorities or law enforcement.</li>
  </ul>

  <h2>4. Do we share your data with third-party providers?</h2>
  <p>
    We value your privacy and never sell your data. However, we do share just what’s needed to make your Planet VG
    experience awesome and secure. Here’s who we might share your data with and why:
  </p>
  <h3>Third-party providers</h3>
  <p>
    To keep things running smoothly, we use trusted third parties to help us with specific tasks. Here’s the breakdown:
  </p>
  <ul>
    <li>Google Firebase (Google LLC): For push notifications to your device.</li>
    <li>Google Cloud Platform (Google Cloud EMEA Limited): For data storage and warehousing.</li>
    <li>HubSpot (Hubspot, inc.): For customer service and marketing communications.</li>
    <li>Stripe (Stripe Payment Europe, Limited): For payment processing and securely storing payment data.</li>
    <li>Scaleway (Scaleway SAS) & NetCup (NetCup GmbH): For secure data storage and processing.</li>
    <li>NetCup (to ensure your plant-based journey doesn’t add more CO2 to the atmosphere, we’ve partnered with NetCup
      to host our servers using 100% renewable green energy. How cool is that?).</li>
    <li>MailTrap (Railsware Products Studio LLC): For sending emails without a hitch.</li>
    <li>Accountants and accounting software (e-boekhouden - SkillSource B.V.): For legal compliance.</li>
  </ul>

  <h2>5. How do we keep your data safe?</h2>
  <p>
    We take your data security seriously and protect it with:
  </p>
  <ul>
    <li>Encrypted servers to safeguard your information.</li>
    <li>Two-factor authentication (2FA) for an extra layer of security.</li>
    <li>Regular security updates to stay ahead of potential threats.</li>
    <li>HTTPS to ensure secure communication between your browser and our servers.</li>
    <li>OAuth2 for secure and trusted login processes.</li>
    <li>Code reviews and analysis: We check our source code against the OWASP Top 10 vulnerabilities and use tools like
      SonarCloud to detect and fix potential security issues.</li>
  </ul>
  <p>
    While we do everything we can to protect your data, no system is entirely foolproof. For your part, always use
    strong, unique passwords and keep them safe. If you ever notice anything suspicious, let us know immediately!
  </p>

  <h2>6. Would you like a vegan (chocolate chip) cookie?</h2>
  <p>
    We use cookies to make Planet VG work better for everyone.
  </p>
  <h3>First party cookies</h3>

  <div class="container-table">
    <table>
      <th>Name</th>
      <th>Provider</th>
      <th>Purpose</th>
      <th>Duration</th>
      <tr>
        <td>auth_session_id_legacy</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>Session</td>
      </tr>
      <tr>
        <td>auth_session_id</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>Session</td>
      </tr>
      <tr>
        <td>kc_restart</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>Session</td>
      </tr>
      <tr>
        <td>keycloak_identity_legacy</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>Session</td>
      </tr>
      <tr>
        <td>keycloak_identity</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>Session</td>
      </tr>
      <tr>
        <td>keycloak_session_legacy</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>2 weeks</td>
      </tr>
      <tr>
        <td>keycloak_session</td>
        <td>Almost Level 5</td>
        <td>Authentication</td>
        <td>2 weeks</td>
      </tr>
    </table>
  </div>

  <h3>Third party cookies (partner.planet.vg only)</h3>
  <div class="container-table">
    <table>
      <th>Name</th>
      <th>Provider</th>
      <th>Purpose</th>
      <th>Duration</th>
      <tr>
        <td>__stripe_mid</td>
        <td>Stripe Payments Europe, Limited</td>
        <td>Payments</td>
        <td>Day</td>
      </tr>
      <tr>
        <td>__stripe_sid</td>
        <td>Stripe Payments Europe, Limited</td>
        <td>Payments</td>
        <td>Day</td>
      </tr>
      <tr>
        <td>stripe.customerportal.csrf</td>
        <td>Stripe Payments Europe, Limited</td>
        <td>Payments</td>
        <td>Session</td>
      </tr>
    </table>
  </div>

  <p>
    You can delete or block cookies via your browser settings but note that some features might not work without them.
  </p>

  <h2>7. How long do we store data?</h2>
  <p>We store your data for as long as necessary to properly execute and maintain our services and agreements. After
    that your personal data will be deleted. </p>
  <p><strong>For users</strong>, this means that your account and user data is immediately deleted or anonymized after
    you delete your account.</p>
  <p><strong>For partners</strong>, this means that as soon as your subscription is canceled, all associated data will
    be anonymized.</p>
  <p>
    We can keep personal data longer if we are legally obliged to do so and taking into account any limitation periods.
    We are, for example, bound by the Dutch fiscal retention obligation of 7 years for data regarding any payments you
    have made.
  </p>
  <p>
    After you have unsubscribed from our newsletter, your email address may be stored by us or the newsletter service
    provider Hubspot in a blocklist, if such action is necessary to prevent future mailings.
  </p>
  <p>
    If we decide to close your account due to misconduct, we may keep your information for a
    period of one year after closure, due to queries and / or in case of a legal claim. In any case, we may keep (some
    of) your personal information for (evidence due to) legal claims.
  </p>

  <h2>8. What are your privacy rights?</h2>
  <p>
    <strong>The right to take your data with you</strong><br />
    You can ask for a copy of your personal data in a structured, easy-to-read format. This means you can share it with
    someone else, if it’s technically possible.
  </p>

  <p><strong>The right to access your data</strong><br />Want to know what personal data we have about you? You can
    request a copy to check how we’re using it and ensure it’s all lawful.</p>

  <p><strong>The right to change or delete your data</strong><br />If your data isn’t accurate or you simply want us to
    delete it, you can ask us to correct or delete your data. </p>

  <p><strong>DIY</strong><br />You can delete your user or partner account directly in your account settings, or at <a
      href="https://account.planet.vg/privacy" target="_blank" rel="noopener">https://account.planet.vg/privacy</a>
    page. Keep in mind, if you delete your account, all related data will be erased or anonymized—except for data that
    we’re required to keep. Collected stamps, vouchers, your subscriptions and other activities that you have performed
    will be deleted as well.</p>

  <p><strong>The right to restrict your data</strong><br />In some cases, you have the right to limit the processing of
    your personal data.</p>

  <p><strong>The right to object to processing</strong><br />Don’t want us using your data anymore? You can ask us to
    stop, especially for things like marketing.</p>

  <p><strong>The right to change your mind</strong><br />If you gave us permission to process your data (like signing up
    for a newsletter), you can change your mind anytime.</p>
  <ul>
    <li><strong>Unsubscribe from our newsletter:</strong> Click the link at the bottom of any newsletter.</li>
    <li><strong>Update Cookie preferences:</strong> Adjust them on our cookie banner or the <em>Privacy Settings</em>
      below.</li>
  </ul>
  <p>You can contact us at privacy&#64;almostlevel5.com to exercise your rights.</p>

  <h2>9. How do we update this policy?</h2>
  <p>
    We may update this policy to reflect changes in our services or regulations. You’ll always be notified via email or
    app notifications for major updates.
  </p>

  <h2>10. Questions?</h2>
  <p>Got questions or concerns? We’re here to help.</p>
  <p><strong>Email:</strong> privacy&#64;almostlevel5.com</p>
</div>